import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {
	Alert,
	FlexColumn,
	Modal,
	RichTextEditor,
	Text,
	TextField,
	useRichTextEditorFunctions,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../generic_modal_conductor';
import {createToast} from '../../toasts/toast';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {buildQuoteBody, buildQuoteSubject} from './buildQuoteBody';
import {RecipientFields} from './RecipientFields';
import {sendQuote} from './sendQuote';

export type SendQuoteContact = {id: string; firstName: string | null; lastName: string | null; email: string | null};

export type SendQuoteModalPayload = {
	/** Relay global ID of the quote being sent. */
	quoteId: string;
	projectName: string;
	/** The project's display indicator (e.g. P2 or a custom id), rendered in the subject. */
	projectIndicator?: string | null;
	clientContacts: SendQuoteContact[];
	/**
	 * The client contact linked to the opportunity (IGNITE-490) — the only pre-selected
	 * recipient. The client's other contacts stay offered in the picker, never pre-added.
	 */
	linkedContactId?: string | null;
	/** Name of the filed PDF, or null when the archive is unreadable — the missing state. */
	pdfFileName: string | null;
	viewerName: string;
	companyName: string;
	/** Optional tracking context, mirroring the decision events' properties. */
	companyProjectId?: number | null;
	companyId?: string | null;
	revisionNumber?: number | null;
};

type Props = SendQuoteModalPayload & {closeModal: () => void};

/**
 * The quiet recipient tally. It sits as the content's last line rather than inside the footer:
 * the modal's footer accepts only its own button components and silently drops anything else.
 */
const RecipientCount = styled.span`
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

/**
 * Section label at true bold. The design calls for bolder-than-medium labels here, and the type
 * ramp's label token tops out at 500 — so this builds on that token and raises only the weight,
 * using the 700 the ramp's own page headings already ship in this face.
 */
export const FieldLabel = styled.span`
	${props => props.theme.sys.font.label['1']};
	font-weight: 700;
	color: ${props => props.theme.sys.color.text.content.main};
`;

/**
 * The compose step of a send: recipient chips over the client's contacts (chosen, never typed), an
 * editable subject and rich-text body pre-filled once from the message template, and the published
 * PDF as a read-only attachment. Send is one gate read off the field itself — at least one
 * recipient, a resolvable attachment, and no send already in flight.
 */
export const SendQuoteModal = ({
	closeModal,
	quoteId,
	projectName,
	projectIndicator,
	clientContacts,
	linkedContactId,
	pdfFileName,
	viewerName,
	companyName,
	companyProjectId,
	companyId,
	revisionNumber,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const {editorRef, getEditorValueAsHtml} = useRichTextEditorFunctions();

	const addressed = useMemo(() => clientContacts.filter(contact => !!contact.email), [clientContacts]);
	const linkedContact = useMemo(
		() => addressed.find(contact => contact.id === linkedContactId) || null,
		[addressed, linkedContactId]
	);

	// To is pre-filled with only the contact linked to the opportunity — a quote is a targeted
	// commercial mail, not a client-wide broadcast, so the client's other contacts stay one
	// picker-click away rather than pre-added. No linked contact (or one without an address)
	// seeds an empty field the user must fill. There is deliberately no Cc/Bcc: every recipient
	// is a client contact addressed directly, though the API still accepts both fields.
	const [to, setTo] = useState<string[]>(() => (linkedContact ? [linkedContact.id] : []));
	const [subject, setSubject] = useState(() =>
		buildQuoteSubject(formatMessage({id: 'send_quote_modal.subject_default'}), projectName, projectIndicator)
	);
	const [submitting, setSubmitting] = useState(false);
	// A refusal the server explained (stale status, contact gone, oversized body) renders inline:
	// the global warning modal would replace this one and destroy the composed draft, and the
	// non-critical path would render behind it where it cannot be read.
	const [errorMessageId, setErrorMessageId] = useState<string | null>(null);

	// Resolved ONCE, on open — the greeting is the user's to adjust afterwards, so a recipient
	// change must never rewrite it under their cursor. See buildQuoteBody.
	const [initialBody] = useState(() =>
		buildQuoteBody(formatMessage({id: 'send_quote_modal.body_template'}), {
			firstRecipientFirstName: linkedContact ? linkedContact.firstName : null,
			projectName,
			companyName,
			userName: viewerName,
			greetingFallback: formatMessage({id: 'send_quote_modal.greeting_fallback'}),
		})
	);

	// One rule read off the field's contents, not three rules derived from its three causes (no
	// client, no contacts, no addressed contacts). The in-flight half is not a nicety: a send is
	// external and non-idempotent, so a second click is a second client email and a second audit
	// row, and this gate is the only place to stop it. The subject half keeps a commercial email
	// from arriving as "(no subject)".
	const canSend = to.length > 0 && subject.trim().length > 0 && !!pdfFileName && !submitting;
	const recipientCount = to.length;

	const handleSend = useCallback(() => {
		if (!canSend) return;
		const bodyHtml = getEditorValueAsHtml();
		// undefined means the editor is not mounted — refuse to send, never treat it as an empty body.
		if (bodyHtml === undefined) return;
		setSubmitting(true);
		setErrorMessageId(null);
		sendQuote(
			{quoteId, toContactIds: to, subject, bodyHtml},
			() => {
				trackEvent('Quote', 'Sent', {
					projectId: companyProjectId ?? null,
					companyId: companyId ?? null,
					revisionNumber: revisionNumber ?? null,
					recipientCount,
				});
				// The modal closes on success, so the toast is the confirmation the user keeps.
				createToast({
					duration: 5000,
					variant: 'banner',
					message: (
						<Alert
							type={Alert.TYPE.SUCCESS}
							text={formatMessage({id: 'send_quote_modal.sent_toast'}, {count: recipientCount})}
						/>
					),
				});
				closeModal();
			},
			validationMessageId => {
				// Stay open on failure so the draft survives. A recognised refusal renders inline
				// below; anything else has already been surfaced by Util.CommitMutation.
				setSubmitting(false);
				setErrorMessageId(validationMessageId);
			}
		);
	}, [
		canSend,
		getEditorValueAsHtml,
		quoteId,
		to,
		subject,
		companyProjectId,
		companyId,
		revisionNumber,
		recipientCount,
		closeModal,
	]);

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'send_quote_modal.title'})}</Modal.Title>
			<Modal.Content data-cy="send-quote-modal">
				<FlexColumn gap="m">
					<Text color="medium">{formatMessage({id: 'send_quote_modal.subtitle'})}</Text>
					<RecipientFields contacts={clientContacts} to={to} onToChange={setTo} />
					{to.length === 0 && <Text color="medium">{formatMessage({id: 'send_quote_modal.empty_recipients'})}</Text>}
					<FlexColumn gap="xs">
						<FieldLabel>{formatMessage({id: 'send_quote_modal.subject'})}</FieldLabel>
						<TextField
							value={subject}
							onValueChange={(value: string) => setSubject(value)}
							maxLength={255}
							data-cy="send-quote-subject"
						/>
					</FlexColumn>
					<FlexColumn gap="xs">
						<FieldLabel>{formatMessage({id: 'send_quote_modal.message'})}</FieldLabel>
						<RichTextEditor
							initialHtml={initialBody}
							pluginToggles={{todoList: false, paragraphStyles: true, link: true}}
							data-cy="send-quote-body"
							ref={editorRef}
						/>
					</FlexColumn>
					<FlexColumn gap="xs">
						<FieldLabel>{formatMessage({id: 'send_quote_modal.attachment'})}</FieldLabel>
						{pdfFileName ? (
							// Read-only by design: the attachment IS the published quote, with no
							// control to swap or remove it — an email announcing a quote must carry it.
							<Text data-cy="send-quote-attachment">{pdfFileName}</Text>
						) : (
							<Text data-cy="send-quote-attachment-missing">
								{formatMessage({id: 'send_quote_modal.attachment_missing'})}
							</Text>
						)}
					</FlexColumn>
					{errorMessageId && (
						<div data-cy="send-quote-error">
							<Alert type={Alert.TYPE.ERROR} text={formatMessage({id: errorMessageId})} />
						</div>
					)}
					<RecipientCount data-cy="send-quote-recipient-count">
						{formatMessage({id: 'send_quote_modal.recipient_count'}, {count: recipientCount})}
					</RecipientCount>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="send-quote-cancel">
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton onPress={handleSend} disabled={!canSend} dataCy="send-quote-confirm">
					{formatMessage({id: 'send_quote_modal.send'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const showSendQuoteModal = (payload: SendQuoteModalPayload): void => {
	showModal({type: MODAL_TYPE.SEND_QUOTE, ...payload});
};
